import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Box, Fab, Grid, IconButton, Paper } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import LinearProgress from "@mui/material/LinearProgress";
import { useGridApiRef } from "@mui/x-data-grid";
import LocalizedDataGrid from "../../../../ui/datagrid/LocalizedDataGrid";
import CSVDownloader from "../../../../ui/CSVDownloader";
import { NoRowOverlay } from "../../../../table/NoRowOverlay";
import CustomFilter from "../../../../table/CustomFilter";
import getUniqueOptions from "../../../../table/getUniqueOptions";
import { getToday } from "../../../../ui/utils/DateUtils";
import { store } from "../../../../../store";
import {
  getSampleShipmentsByCenter,
  getSampleShipmentsByStudy,
  getSampleShipments,
} from "../../../../../redux/actions/sampleShipment";
import dayjs from "dayjs";

const SPACING = 50;

const createEmptyComboFilters = () => ({
  status: "",
  carrier: "",
  centerNumber: "",
  studyCode: "",
});

const parseAddress = (address) => {
  if (!address) return {};
  if (typeof address === "object") return address;

  try {
    return JSON.parse(address);
  } catch (error) {
    console.error("Error parsing sample shipment address:", error);
    return {};
  }
};

const toOptions = (rows, field) =>
  getUniqueOptions(rows, field).filter((option) => option.value !== "");

export const ScreenSampleShipments = () => {
  const apiRef = useGridApiRef();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const studyId = location.state?.studyId;
  const studyCode = location.state?.studyCode;
  const centerId = location.state?.centerId;
  const centerNumber = location.state?.centerNumber;
  const isLoading = useSelector(
    (state) => state.sampleShipment.isLoadingSampleShipments,
  );
  const sampleShipments = useSelector(
    (state) => state.sampleShipment.sampleShipments,
  );
  const [comboFilters, setComboFilters] = React.useState(
    createEmptyComboFilters,
  );
  const [statusOptions, setStatusOptions] = React.useState([]);
  const [carrierOptions, setCarrierOptions] = React.useState([]);
  const [centerNumberOptions, setCenterNumberOptions] = React.useState([]);
  const [studyCodeOptions, setStudyCodeOptions] = React.useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = React.useState([]);

  const handleShipmentClick = React.useCallback(
    (shipment) => {
      const shipmentId = shipment.sampleShipmentId || shipment.id;
      const reference = shipment.reference || shipmentId;

      navigate(`/sample-shipments/${encodeURIComponent(reference)}`, {
        state: {
          sampleShipmentId: shipmentId,
          studyCode,
          centerNumber,
          studyId,
          centerId,
        },
      });
    },
    [centerId, centerNumber, navigate, studyCode, studyId],
  );

  const updateComboFilter = React.useCallback((field, value) => {
    setComboFilters((currentFilters) => ({
      ...currentFilters,
      [field]: value || "",
    }));
  }, []);

  const activeFilterLabels = React.useMemo(() => {
    return [
      comboFilters.centerNumber,
      comboFilters.studyCode,
      comboFilters.status,
      comboFilters.carrier,
    ].filter(Boolean);
  }, [comboFilters]);

  const selectedRowsLabel = React.useMemo(() => {
    if (selectedRowKeys.length === 0) {
      return "";
    }

    const count = selectedRowKeys.length.toLocaleString();

    if (i18n.language?.startsWith("fr")) {
      return `${count} ligne${selectedRowKeys.length > 1 ? "s" : ""} sélectionnée${
        selectedRowKeys.length > 1 ? "s" : ""
      }`;
    }

    return `${count} row${selectedRowKeys.length > 1 ? "s" : ""} selected`;
  }, [i18n.language, selectedRowKeys.length]);

  const showFilterStatus =
    activeFilterLabels.length > 0 || selectedRowKeys.length > 0;

  const handleResetFilters = React.useCallback(() => {
    setComboFilters(createEmptyComboFilters());
    apiRef.current?.setFilterModel?.({ items: [] });
  }, [apiRef]);

  const rows = React.useMemo(() => {
    return (sampleShipments || []).map((shipment) => {
      const address = parseAddress(shipment.address || shipment.pickupAddress);
      const shipmentCenter = shipment.center || shipment.Center || {};
      const shipmentStudy = shipmentCenter.Study || shipmentCenter.study || {};
      const pickupDate =
        shipment.pickupDatetimeStart ||
        shipment.expectedShippingDate ||
        shipment.pickupDate ||
        null;
      const receiveDate = shipment.receiveDatetime || null;

      return {
        key: String(shipment.id),
        id: shipment.id,
        sampleShipmentId: shipment.id,
        reference: shipment.reference || "",
        status: shipment.status || "",
        centerNumber: String(
          address.centerNumber || shipmentCenter.centerNumber || centerNumber || "",
        ),
        studyCode:
          shipment.studyCode ||
          shipmentStudy.cdlStudyCode ||
          shipmentStudy.sponsorStudyCode ||
          studyCode ||
          "",
        pickupDate: pickupDate ? new Date(pickupDate) : null,
        receiveDate: receiveDate ? new Date(receiveDate) : null,
        carrier: shipment.carrier || "",
        airwaybill: shipment.trackingNumber || shipment.airwaybill || "",
      };
    });
  }, [centerNumber, sampleShipments, studyCode]);

  const displayedSampleShipments = React.useMemo(() => {
    return rows.filter((shipment) => {
      if (
        comboFilters.centerNumber &&
        shipment.centerNumber !== comboFilters.centerNumber
      ) {
        return false;
      }

      if (comboFilters.status && shipment.status !== comboFilters.status) {
        return false;
      }

      if (
        comboFilters.studyCode &&
        shipment.studyCode !== comboFilters.studyCode
      ) {
        return false;
      }

      if (comboFilters.carrier && shipment.carrier !== comboFilters.carrier) {
        return false;
      }

      return true;
    });
  }, [comboFilters, rows]);

  const downloadSampleShipments = React.useMemo(
    () =>
      displayedSampleShipments
        .filter((shipment) => selectedRowKeys.includes(shipment.key))
        .map(({ key, samples, allSamples, ...shipment }) => shipment),
    [displayedSampleShipments, selectedRowKeys],
  );

  React.useEffect(() => {
    const visibleKeys = new Set(
      displayedSampleShipments.map((shipment) => shipment.key),
    );
    setSelectedRowKeys((currentKeys) =>
      currentKeys.filter((rowKey) => visibleKeys.has(rowKey)),
    );
  }, [displayedSampleShipments]);

  const columns = React.useMemo(() => {
    const comboFilterOperator = (field, label, options) => [
      {
        label,
        value: field,
        getApplyFilterFn: (filterItem) => {
          if (!filterItem.value) return null;
          return ({ value }) => value === filterItem.value;
        },
        InputComponent: (props) => (
          <CustomFilter
            label={label}
            options={options}
            {...props}
            item={{
              ...props.item,
              value: props.item.value || comboFilters[field],
            }}
            applyValue={(nextItem) => {
              props.applyValue(nextItem);
              updateComboFilter(field, nextItem.value);
            }}
          />
        ),
      },
    ];

    return [
      {
        field: "reference",
        headerName: t("reference", "Reference"),
        minWidth: 130,
      },
      {
        field: "status",
        headerName: t("status"),
        minWidth: 150,
        filterable: true,
        filterOperators: comboFilterOperator(
          "status",
          t("status"),
          statusOptions,
        ),
      },
      {
        field: "centerNumber",
        headerName: t("center"),
        minWidth: 100,
        filterable: true,
        filterOperators: comboFilterOperator(
          "centerNumber",
          t("center"),
          centerNumberOptions,
        ),
      },
      {
        field: "studyCode",
        headerName: t("studyCode", "Study code"),
        minWidth: 130,
        filterable: true,
        filterOperators: comboFilterOperator(
          "studyCode",
          t("studyCode", "Study code"),
          studyCodeOptions,
        ),
      },
      {
        field: "pickupDate",
        type: "date",
        headerName: t("pickupDate", "Pick-up Date"),
        minWidth: 130,
        renderCell: (params) =>
          params.value ? dayjs(params.value).format("DD/MM/YYYY") : "",
      },
      {
        field: "receiveDate",
        type: "date",
        headerName: t("receiveDate", "Receive date"),
        minWidth: 130,
        renderCell: (params) =>
          params.value ? dayjs(params.value).format("DD/MM/YYYY") : "",
      },
      {
        field: "carrier",
        headerName: t("carrier"),
        minWidth: 130,
        filterable: true,
        filterOperators: comboFilterOperator(
          "carrier",
          t("carrier"),
          carrierOptions,
        ),
      },
      {
        field: "airwaybill",
        headerName: t("airwaybill"),
        minWidth: 130,
      },
    ];
  }, [
    carrierOptions,
    centerNumberOptions,
    comboFilters,
    statusOptions,
    studyCodeOptions,
    t,
    updateComboFilter,
  ]);

  React.useEffect(() => {
    if (centerId) {
      store.dispatch(getSampleShipmentsByCenter(centerId));
    } else if (studyId) {
      store.dispatch(getSampleShipmentsByStudy(studyId));
    } else {
      store.dispatch(getSampleShipments());
    }
  }, [centerId, studyId]);

  React.useEffect(() => {
    setStatusOptions(toOptions(rows, "status"));
    setCarrierOptions(toOptions(rows, "carrier"));
    setCenterNumberOptions(toOptions(rows, "centerNumber"));
    setStudyCodeOptions(toOptions(rows, "studyCode"));
  }, [rows]);

  const showNewButton = Boolean(studyId || centerId);

  return (
    <Grid item xs={12}>
      <Box
        sx={{
          position: "relative",
          pb: downloadSampleShipments.length > 0 ? 12 : 0,
        }}>
        <Paper sx={{ display: "flex", flexDirection: "column" }}>
          <Box sx={{ width: "100%", height: "90vh", position: "relative" }}>
            <LocalizedDataGrid
              apiRef={apiRef}
              disableRowSelectionOnClick
              slots={{
                noRowsOverlay: NoRowOverlay,
                loadingOverlay: LinearProgress,
              }}
              rows={displayedSampleShipments}
              columns={columns}
              loading={isLoading}
              initialState={{
                pagination: {
                  paginationModel: { page: 0, pageSize: 50 },
                },
              }}
              getRowId={(row) => row.key}
              pageSizeOptions={[20, 50, 100]}
              checkboxSelection
              hideFooterSelectedRowCount
              onCellClick={(params) => {
                if (params.field === "__check__") return;
                handleShipmentClick(params.row);
              }}
              onRowSelectionModelChange={(newSelection) => {
                setSelectedRowKeys(newSelection);
              }}
              rowHeight={40}
              sx={{
                "& .MuiDataGrid-row": {
                  cursor: "pointer",
                },
              }}
            />
            {showFilterStatus && (
              <Box
                sx={{
                  position: "absolute",
                  left: 16,
                  bottom: 10,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  maxWidth: "70%",
                  color: "text.secondary",
                  fontSize: "0.875rem",
                  zIndex: 2,
                }}>
                {activeFilterLabels.length > 0 && (
                  <IconButton
                    aria-label="Reset filters"
                    onClick={handleResetFilters}
                    size="small"
                    sx={{ p: 0.25, flexShrink: 0 }}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                )}
                <Box
                  component="span"
                  sx={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}>
                  {activeFilterLabels.length > 0
                    ? `Filters: ${activeFilterLabels.join(", ")}`
                    : selectedRowsLabel}
                </Box>
                {activeFilterLabels.length > 0 && selectedRowsLabel && (
                  <Box
                    component="span"
                    sx={{ color: "text.primary", flexShrink: 0 }}>
                    {selectedRowsLabel}
                  </Box>
                )}
              </Box>
            )}
          </Box>
        </Paper>
        {downloadSampleShipments.length > 0 && (
          <Box sx={{ position: "absolute", left: 0, bottom: 16, zIndex: 3 }}>
            <CSVDownloader
              data={downloadSampleShipments}
              filename={`${studyCode}${centerNumber ? `_${centerNumber}` : ""}_sample_shipments_${getToday()}`}
              name={t("sampleShipments", "Sample Shipments")}
            />
          </Box>
        )}
      </Box>
      {showNewButton && (
        <Fab
          color="primary"
          aria-label="add"
          onClick={() => {
            navigate("/sample-shipments/new", {
              state: { centerId, studyId, centerNumber, studyCode },
            });
          }}
          style={{ position: "absolute", right: SPACING, bottom: SPACING }}>
          <AddIcon />
        </Fab>
      )}
    </Grid>
  );
};
